import { translateBackend } from '@assets/i18n/config';

import { useAccordionTokens } from '@ovComponents/2-component/accordion/accordian.tokens';
import {
  Accordion,
  AccordionSummary,
} from '@ovComponents/2-component/accordion/accordion';
import { Box, Grid, IconButton, Typography, useTheme } from 'hephaestus/web';
import {
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
} from 'hephaestus/webIcons';
import { orderBy } from 'lodash-es';
import { useState } from 'react';
import type z from 'zod/v3';
import { WorkflowEditFlow } from './workflowEditFlow';
import type {
  changesSchema,
  translationSchema,
} from './workflowReview.schemas';
import { isVisibleFieldChange } from './workflowReviewChangeUtils';

const workflowReviewCardTitles = {
  mainTitle: {
    fontWeight: 800,
  },
  arrow: {
    fontWeight: 500,
  },
  oldValue: {
    fontWeight: 400,
  },
  newValue: {
    fontWeight: 600,
  },
};

const WorkflowReviewCard = ({
  changes,
  stepName,
  firstSubStepIdOfStep,
  workflowCompletion,
  stepId,
  refetchLogs,
  enableEdit,
}: {
  changes: z.infer<typeof changesSchema>;
  stepName: z.infer<typeof translationSchema> | undefined;
  firstSubStepIdOfStep?: string;
  workflowCompletion: any;
  stepId: string;
  refetchLogs: () => void;
  enableEdit?: boolean;
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const [expanded, setExpanded] = useState(false);
  const theme = useTheme();
  const tokens = useAccordionTokens(theme);
  const [targetSubStepId, setTargetSubStepId] = useState<string | undefined>(
    undefined,
  );
  const [targetStepId, setTargetStepId] = useState<string>('');
  const [reviewStepId, setReviewStepId] = useState<string>('');
  type VisibleChangeItem = {
    currentStepId: string;
    key: string;
    originalAndLastUpdated: (typeof changes)[string]['changes'][string]['originalAndLastUpdated'];
  };
  const isVisibleChangeItem = (
    value: VisibleChangeItem | null,
  ): value is VisibleChangeItem => value !== null;
  const visibleChanges = Object.keys(changes).flatMap((currentStepId) =>
    orderBy(Object.keys(changes[currentStepId].changes))
      .map((key) => {
        const fieldChanges = changes[currentStepId].changes[key];
        if (!isVisibleFieldChange(fieldChanges)) return null;
        const { originalAndLastUpdated } = fieldChanges;
        return { currentStepId, key, originalAndLastUpdated };
      })
      .filter(isVisibleChangeItem),
  );

  if (visibleChanges.length === 0) return null;

  return (
    <>
      <Accordion
        expanded={expanded}
        onChange={() => setExpanded((prev) => !prev)}
        variant='workflow'
        sx={{
          '.MuiAccordionSummary-root': { borderTop: 'none !important' },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls='panel1a-content'
          id='panel1a-header-2'
          sx={{
            '.MuiAccordionSummary-content': {
              justifyContent: 'space-between !important',
            },
            '.MuiAccordionSummary-expandIconWrapper': {
              position: 'absolute',
              top: '10px',
              right: '15px',
            },
          }}
        >
          <Box sx={{ width: '100%' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <Typography
                variant='bodyLarge'
                sx={workflowReviewCardTitles.mainTitle}
              >
                {translateBackend(stepName)}
              </Typography>
              {enableEdit && (
                <EditIcon
                  fontSize='small'
                  sx={{
                    cursor: 'pointer',
                    marginRight: '50px',
                    '&:hover': {
                      color: 'primary.main',
                    },
                  }}
                  onClick={(event) => {
                    setTargetStepId(stepId);
                    setTargetSubStepId(firstSubStepIdOfStep);
                    setReviewStepId(workflowCompletion?.currentSubStep?.id);
                    setOpen(true);
                    event.stopPropagation();
                    event.preventDefault();
                  }}
                />
              )}
            </Box>
            {expanded && (
              <>
                <hr
                  style={{
                    borderTop: `1px solid ${tokens.multipleBorderColor}`,
                  }}
                />
                <Box
                  sx={{
                    mt: 2,
                    width: '100%',
                  }}
                >
                  <Grid container spacing={1}>
                    {visibleChanges.map(
                      (
                        { currentStepId, key, originalAndLastUpdated },
                        index,
                      ) => {
                        return (
                          <Grid
                            key={`${currentStepId}-${key}`}
                            sx={{ mt: index === 0 ? 0 : 1 }}
                            size={12}
                          >
                            {/* Field Label */}
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                              }}
                            >
                              <Typography
                                variant='bodyMedium'
                                sx={{
                                  ...workflowReviewCardTitles.mainTitle,
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  '& .MuiIconButton-root': {
                                    visibility: 'hidden',
                                  },
                                  '&:hover .MuiIconButton-root': {
                                    visibility: 'visible',
                                  },
                                  fontWeight: 500,
                                }}
                              >
                                {translateBackend(
                                  originalAndLastUpdated.key,
                                )?.trim()}
                                {enableEdit && (
                                  <IconButton
                                    size='xsmall'
                                    aria-label='edit field'
                                    data-testid='workflow-review-edit-field-button'
                                    onClick={(event) => {
                                      setTargetSubStepId(
                                        originalAndLastUpdated.subStep?.id ??
                                          '',
                                      );
                                      setTargetStepId(currentStepId);
                                      setReviewStepId(
                                        workflowCompletion?.currentSubStep?.id,
                                      );
                                      setOpen(true);
                                      event.stopPropagation();
                                      event.preventDefault();
                                    }}
                                  >
                                    <EditIcon fontSize='inherit' />
                                  </IconButton>
                                )}
                              </Typography>
                            </Box>
                            {/* Old → New Values */}
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                minWidth: 0,
                                width: '100%',
                              }}
                            >
                              <Typography
                                variant='bodyMedium'
                                sx={{
                                  ...workflowReviewCardTitles.oldValue,
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                  overflowWrap: 'break-word',
                                  display: 'block',
                                  minWidth: 0,
                                  maxWidth: 'calc(50% - 20px)',
                                  overflow: 'hidden',
                                  fontWeight: 500,
                                }}
                              >
                                {(() => {
                                  const value = originalAndLastUpdated.oldValue;
                                  if (value === null || value === '')
                                    return <span>null</span>;
                                  if (typeof value === 'boolean')
                                    return value ? 'true' : 'false';
                                  return value;
                                })()}
                              </Typography>
                              <Typography
                                variant='bodyMedium'
                                sx={{
                                  ...workflowReviewCardTitles.arrow,
                                  flexShrink: 0,
                                  fontWeight: 500,
                                }}
                                component='span'
                                aria-label='arrow'
                              >
                                &rarr;
                              </Typography>
                              <Typography
                                variant='bodyMedium'
                                sx={{
                                  ...workflowReviewCardTitles.newValue,
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                  overflowWrap: 'break-word',
                                  display: 'block',
                                  minWidth: 0,
                                  maxWidth: 'calc(50% - 20px)',
                                  flex: 1,
                                  overflow: 'hidden',
                                  fontWeight: 500,
                                }}
                              >
                                {(() => {
                                  const value = originalAndLastUpdated.newValue;
                                  if (value === null || value === '')
                                    return <em>null</em>;
                                  if (typeof value === 'boolean')
                                    return value ? 'true' : 'false';
                                  return value;
                                })()}
                              </Typography>
                            </Box>
                          </Grid>
                        );
                      },
                    )}
                  </Grid>
                </Box>
              </>
            )}
          </Box>
        </AccordionSummary>
      </Accordion>
      <WorkflowEditFlow
        open={open}
        setOpen={setOpen}
        objectId={workflowCompletion?.objectId}
        objectType={workflowCompletion?.objectType}
        refetch={refetchLogs}
        targetWorkflowComplation={workflowCompletion}
        targetSubStepId={targetSubStepId || firstSubStepIdOfStep}
        targetStepId={targetStepId || stepId}
        reviewStepId={reviewStepId}
      />
    </>
  );
};

export default WorkflowReviewCard;
